import { useEffect, useState } from "react";

const KEY = "sa.session.visited";
const MAX = 60;

export function readSessionVisited(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? (arr as string[]) : [];
  } catch {
    return [];
  }
}

function write(list: string[]) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(list.slice(-MAX)));
  } catch {
    /* ignore */
  }
}

/**
 * Session-scoped "already seen" set used by every next-article surface so the
 * reader never gets bounced between the same two articles (A -> B -> A).
 * Returns the set as it was BEFORE the current article was recorded, so the
 * current page can still exclude everything the reader already came from.
 */
export function useSessionVisited(currentSlug: string): Set<string> {
  const [visited, setVisited] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const arr = readSessionVisited();
    setVisited(new Set(arr));
    write(Array.from(new Set([...arr, currentSlug])));
  }, [currentSlug]);

  return visited;
}

/**
 * Pick the first candidate the reader has not already seen this session.
 * Falls back to the first candidate only when everything is exhausted.
 */
export function pickUnseen<T extends { slug: string }>(
  candidates: Array<T | null | undefined>,
  visited: Set<string>,
  currentSlug: string,
): T | null {
  const list = candidates.filter(Boolean) as T[];
  const fresh = list.find((c) => c.slug !== currentSlug && !visited.has(c.slug));
  if (fresh) return fresh;
  return list.find((c) => c.slug !== currentSlug) ?? null;
}
